<script setup lang="ts">
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import PageLoading from "@/components/Common/PageLoading.vue";
import { toast } from "vue-sonner";
import { useItemStore } from "@/stores/management/item";
import { ItemDataResponse } from "@/types/interface/item";
import { useUnitStore } from "@/stores/master/unit";
import { useCategoryGroupStore } from "@/stores/master/category-group-store";
import { mapOptionMultiselect, urlToObject } from "@/utils/helper";
import ImageUploader from "@/components/Common/ImageUploader.vue";

const idItem = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const itemStore = useItemStore();
const detailItem = ref<ItemDataResponse>();
const unitStore = useUnitStore();
const groupCategoryStore = useCategoryGroupStore();
const listGroupCategory = ref<any>();
const listUnit = ref<any>();
const previewUrl = ref("");

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    // msg_code: zod.string().nonempty({ message: "MSG Code is required" }),
    impa_code: zod.string().nonempty({ message: "Impa Code is required" }),
    group_category_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    unit_type_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    size: zod.string().nonempty({ message: "Size is required" }),
    remarks: zod.string().nonempty({ message: "Remarks is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: msg_code } = useField<string>("msg_code");
const { value: impa_code } = useField<string>("impa_code");
const { value: group_category_id } = useField<string>("group_category_id");
const { value: unit_type_id } = useField<string>("unit_type_id");
const { value: size } = useField<string>("size");
const { value: image_file } = useField<any>("image_file");
const { value: remarks } = useField<string>("remarks");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const formData = new FormData();
  formData.append("name", name.value);
  formData.append("msg_code", msg_code.value);
  formData.append("impa_code", impa_code.value);
  formData.append("group_category_id", group_category_id.value?.value);
  formData.append("unit_type_id", unit_type_id.value?.value);
  formData.append("size", size.value);
  formData.append("remarks", remarks.value);

  if (image_file.value) {
    formData.append("image_file", image_file.value);
  }
  const responseApi = ref<any>();

  if (isEdit.value) {
    responseApi.value = await itemStore.dispatchUpdate(idItem.value, formData);
  } else {
    responseApi.value = await itemStore.dispatchCreate(formData);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value ? "Item Successfully Updated" : "Item Successfully Added"
      );
      router.push("/management/item");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadGroupCategory = async () => {
  const { success, message } =
    await groupCategoryStore.dispatchGetCategoryGroups(1, 99);
  if (success) {
    listGroupCategory.value = mapOptionMultiselect(
      await groupCategoryStore.categoryGroups.items,
      "id",
      "name",
      "category_name"
    );
  }
};

const loadUnit = async () => {
  const { success, message } = await unitStore.dispatchGetAll(1, 99);
  if (success) {
    listUnit.value = mapOptionMultiselect(
      await unitStore.units.items,
      "id",
      "name"
    );
  }
};

loadUnit();
loadGroupCategory();

const setImage = (file) => {
  previewUrl.value = "";
  image_file.value = file;
};

const loadDetailItem = async () => {
  const { success, message } = await itemStore.dispatchDetail(idItem.value);
  if (success) {
    detailItem.value = await itemStore?.item?.data;
    name.value = detailItem.value?.name;
    impa_code.value = detailItem.value?.impa_code;
    msg_code.value = detailItem.value?.msg_code;
    group_category_id.value = mapOptionMultiselect(
      detailItem.value?.groupCategory,
      "id",
      "name"
    );
    unit_type_id.value = mapOptionMultiselect(
      detailItem.value?.unitType,
      "id",
      "name"
    );
    size.value = detailItem.value?.size;
    image_file.value = await urlToObject(detailItem.value?.image_link);
    previewUrl.value = detailItem.value?.image_link;
    remarks.value =
      detailItem.value?.remarks == null ? "" : detailItem.value?.remarks;
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idItem.value = router.currentRoute.value.params.id.toString();
    loadDetailItem();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card card-with-loading bg-white border-0 rounded-3 mb-4">
        <PageLoading text="Processing Data..." v-if="isLoading" />
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Item</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/management/item"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Item
            </a>
          </div>
          <form @submit="onSubmit" class="row g-3 needs-validation form">
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="msg_code" class="form-label label text-secondary"
                    >MSG Code</label
                  >
                  <div class="position-relative">
                    <input
                      name="msg_code"
                      v-model="msg_code"
                      placeholder="MSG Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.msg_code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="impa_code" class="form-label label text-secondary"
                    >Impa Code <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="impa_code"
                      v-model="impa_code"
                      placeholder="Impa Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.impa_code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="group_category_id"
                    class="form-label label text-secondary"
                    >Group Category <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listGroupCategory"
                      name="group_category_id"
                      v-model="group_category_id"
                      placeholder="Group Category"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{
                    errors.group_category_id
                  }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label
                    for="unit_type_id"
                    class="form-label label text-secondary"
                    >Unit <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <vSelect
                      :options="listUnit"
                      name="unit_type_id"
                      v-model="unit_type_id"
                      placeholder="Unit"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{
                    errors.group_category_id
                  }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="size" class="form-label label text-secondary"
                    >Size <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="size"
                      v-model="size"
                      placeholder="Size"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.size }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="remarks" class="form-label label text-secondary"
                    >Remarks <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <textarea
                      name="remarks"
                      v-model="remarks"
                      placeholder="Remarks"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.remarks }}</span>
                </div>
              </div>
              <div class="col-6">
                <label for="remarks" class="form-label label text-secondary"
                  >Image <span class="text-danger">*</span></label
                >
                <div v-if="previewUrl">
                  <img :src="previewUrl" alt="image-upload" />
                </div>
                <ImageUploader @file-result="setImage" />
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
